"use client";

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const TOAST_ID = "pwa-install-prompt";
const SHOW_DELAY_MS = 1500;
const SESSION_KEY = "br-pwa-install-prompted-session";
const DAY_KEY = "br-pwa-install-prompted-day";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  if (window.matchMedia("(display-mode: fullscreen)").matches) return true;
  // iOS Safari home-screen launch
  if (
    "standalone" in navigator &&
    Boolean((navigator as Navigator & { standalone?: boolean }).standalone)
  ) {
    return true;
  }
  return false;
}

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function alreadyPrompted(): boolean {
  try {
    if (sessionStorage.getItem(SESSION_KEY) === "1") return true;
    if (localStorage.getItem(DAY_KEY) === todayKey()) return true;
  } catch {
    // private mode / blocked storage
  }
  return false;
}

function markPrompted() {
  try {
    sessionStorage.setItem(SESSION_KEY, "1");
    localStorage.setItem(DAY_KEY, todayKey());
  } catch {
    // ignore
  }
}

function getInstallHowTo(): { title: string; steps: string[] } {
  const ua = navigator.userAgent || "";
  const isIOS =
    /iPad|iPhone|iPod/i.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);

  if (isIOS) {
    return {
      title: "Add to Home Screen (iPhone / iPad)",
      steps: [
        "Open this site in Safari (not Chrome).",
        "Tap the Share button.",
        "Tap “Add to Home Screen”, then Add.",
      ],
    };
  }

  if (isAndroid) {
    return {
      title: "Install on Android",
      steps: [
        "Tap the browser menu (⋮).",
        "Tap “Install app” or “Add to Home screen”.",
        "Confirm Install.",
      ],
    };
  }

  return {
    title: "Install from the browser",
    steps: [
      "Open the browser menu (⋮).",
      "Choose “Install page as app…” or use the install icon in the address bar.",
    ],
  };
}

function InstallToastCard({
  getDeferred,
  onDone,
}: {
  getDeferred: () => BeforeInstallPromptEvent | null;
  onDone: () => void;
}) {
  const [mode, setMode] = useState<"prompt" | "howto">("prompt");
  const howTo = getInstallHowTo();

  const onInstall = () => {
    const event = getDeferred();
    if (event) {
      void event
        .prompt()
        .then(async () => {
          await event.userChoice;
          onDone();
        })
        .catch(() => {
          // Native prompt unavailable after all — show manual steps
          setMode("howto");
        });
      return;
    }
    // Mobile Safari / many mobile browsers never fire beforeinstallprompt
    setMode("howto");
  };

  if (mode === "howto") {
    return (
      <div className="flex w-[min(380px,calc(100vw-2rem))] flex-col gap-3 rounded-xl border border-border bg-popover p-4 text-popover-foreground shadow-lg">
        <div className="space-y-2">
          <p className="text-sm font-semibold leading-snug">{howTo.title}</p>
          <ol className="list-decimal space-y-1.5 pl-4 text-sm leading-snug text-muted-foreground">
            {howTo.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onDone}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-[min(380px,calc(100vw-2rem))] flex-col gap-3 rounded-xl border border-border bg-popover p-4 text-popover-foreground shadow-lg">
      <div className="space-y-1">
        <p className="text-sm font-semibold leading-snug">
          Install the admin app
        </p>
        <p className="text-sm leading-snug text-muted-foreground">
          Add it to your desktop or home screen for quicker access.
        </p>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onDone}
          className="rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          Not now
        </button>
        <button
          type="button"
          onClick={onInstall}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
        >
          Install
        </button>
      </div>
    </div>
  );
}

export function InstallPromptToast() {
  const deferredRef = useRef<BeforeInstallPromptEvent | null>(null);
  const shownRef = useRef(false);

  useEffect(() => {
    if (isStandalone() || alreadyPrompted()) return;

    const done = () => {
      markPrompted();
      toast.dismiss(TOAST_ID);
    };

    const showInstallToast = () => {
      if (shownRef.current || alreadyPrompted()) return;
      shownRef.current = true;
      markPrompted();

      toast.custom(
        () => (
          <InstallToastCard
            getDeferred={() => deferredRef.current}
            onDone={done}
          />
        ),
        {
          id: TOAST_ID,
          duration: Infinity,
          className: "!bg-transparent !border-0 !shadow-none !p-0",
        }
      );
    };

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      deferredRef.current = event as BeforeInstallPromptEvent;
    };

    const onAppInstalled = () => {
      deferredRef.current = null;
      done();
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    const timer = window.setTimeout(showInstallToast, SHOW_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
      toast.dismiss(TOAST_ID);
    };
  }, []);

  return null;
}
